import { useEffect, useState } from 'react';
import {
  environment,
  fetchLoyaltyHubRequest,
  fetchRewardAccountRequest,
  fetchRewardRlhRequest,
  fetchViewRlhRewardsRequest,
  webFormatCurrency,
  lhSsoLoginRequest,
  RootState,
  renderDescriptionParagraphs,
  useProductName,
} from '@rb-serve-app/core';
import { useSelector, useDispatch } from 'react-redux';
import Breadcrumbs from './BreadCrumbs';
import Spinner from './Spinner';
import ActiveRewardTile from './ActiveRewardTile';
import FlippedToolTip from './FlippedToolTip';
import CommanModal from './CommanModal';
import { useNavigate, useLocation } from 'react-router-dom';
import InlineError from './ErrorComponents/InlineError';
import { EventType, useAnalytics } from '../ui/Analytics';
import { logBrazeCustomEvent } from '../config/braze';

const Rewards = () => {
  const { trackEvent } = useAnalytics();
  const dispatch = useDispatch();
  const { contentfulData } = useSelector(
    (state: RootState) => state.contentful
  );
  const { entries, assets } = contentfulData;

  const {
    rb_rewards,
    rb_common,
    rb_transaction_history,
    rb_roam_like_home,
    rb_linkAccount,
  } = entries;
  const { accountId, customerId, accountDetails } = useSelector(
    (state: any) => state.user
  );
  const { loading } = useSelector((state: RootState) => state.rewards);
  const { bankAccountDetail, bankAccountDetailLoading, viewRlhRewards } =
    useSelector((state: RootState) => state.profile);
  const { linkScreenIndicator } = useSelector((state: any) => state.banLinkage);
  const { rewardAccountDetails, loading: rewardsAccLoading } = useSelector(
    (state: RootState) => state.banLinkage
  );
  const {
    loyaltyHubData,
    isUserEligible,
    ssoRewardLhData,
    ssoLoginData,
    ssoLoginLoading,
    ssoLoginError,
    loading: ssoIntLoading,
    error,
  } = useSelector((state: RootState) => state.loyaltyHub);
  const { rewardBalance } =
    loyaltyHubData?.pointDetails?.pointsDetailsResponse || {};

  const { statusCode } = loyaltyHubData?.pointDetails || {};

  const { cardStructure, productName } = useProductName();
  const lang = localStorage.getItem('language') || 'en-US';

  const [activeTile, setActiveTile] = useState<null | 'rewards' | 'bonus'>(
    null
  );
  const [showTooltipMobile, setShowTooltipMobile] = useState<
    null | 'rewards' | 'bonus'
  >(null);
  const [shouldSubmitSSO, setShouldSubmitSSO] = useState(false);
  const [showErrModal, setShowErrModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showApiErrorBanner, setShowApiErrorBanner] = useState(false);
  const [showLinkAccStatusBanner, setShowLinkAccStatusBanner] = useState(false);
  const [showIgniteBannerMsg, setShowIgniteBannerMsg] = useState(false);
  const [showLinkAccNotEligibleBanner, setShowLinkAccNotEligibleBanner] =
    useState(false);
  const location = useLocation();

  const handleClickEvent = (action: string) => {
    trackEvent(
      EventType.CLICK_EVENT,
      ['rewards landing page', 'rewards'],
      window.location.href,
      `rewards landing page | ${action}`
    );
  };

  const trackPageView = (data: any) => {
    trackEvent(
      EventType.PAGE_VIEW,
      ['rewards landing page', 'rewards'],
      window.location.href,
      undefined,
      false,
      {},
      data
    );
  };

  useEffect(() => {
    trackPageView('');
    logBrazeCustomEvent('viewed_rewards');
    if (statusCode && statusCode != 200) {
      errorData(
        'loyaltyhub',
        'rlhStatus',
        statusCode?.toString(),
        'rlhStatus',
        'Data Not Found'
      );
    }
  }, []);

  const errorData = (
    category: string,
    type: string,
    code: string,
    api: string,
    message: string
  ) => {
    let errorObject = {};
    errorObject = {
      category: category,
      type: type,
      code: code,
      API: api,
      field: '',
      message: message,
    };
    trackPageView(errorObject);
  };

  useEffect(() => {
    if (error) {
      setShowApiErrorBanner(true);
    } else {
      setShowApiErrorBanner(false);
    }
  }, [error]);

  useEffect(() => {
    if (accountId) {
      if (
        entries?.rb_feature_flagging?.featureFlagging?.rewards
          ?.loyalty_rewards_hub === true
      ) {
        // sso login check

        dispatch(fetchRewardRlhRequest({ accountId, customerId }));
      }
      // rlh status check - this will return you the bank account response (i.e) linkageStatus
      if (!rewardAccountDetails) {
        const strippeduser_id = accountId.replace(/^0+/, '') || '0';
        dispatch(fetchRewardAccountRequest({ user_id: strippeduser_id }));
      }
      dispatch(fetchLoyaltyHubRequest({ accountId, customerId }));
    }
  }, [accountId, customerId, dispatch]);

  useEffect(() => {
    // Check navigation state from LinkAccount page
    const navState = location.state as { linkScreenIndicator?: string } | null;
    const indicator = navState?.linkScreenIndicator || linkScreenIndicator;

    let timeoutId: NodeJS.Timeout | null = null;

    if (indicator === 'LINK_TRUE') {
      setShowLinkAccStatusBanner(true);
      setShowIgniteBannerMsg(false);
      setShowLinkAccNotEligibleBanner(false);
      timeoutId = setTimeout(() => setShowLinkAccStatusBanner(false), 30000);
    } else if (indicator === 'LINK_ALMOST_THERE') {
      setShowLinkAccStatusBanner(false);
      setShowIgniteBannerMsg(true);
      setShowLinkAccNotEligibleBanner(false);
      timeoutId = setTimeout(() => setShowIgniteBannerMsg(false), 30000);
    } else if (
      indicator === 'LINK_FALSE' ||
      indicator === 'LINK_NOT_ELIGIBLE' ||
      indicator === 'NOT_ELIGIBLE' ||
      indicator === 'LINKED_ALREADY'
    ) {
      setShowLinkAccStatusBanner(false);
      setShowIgniteBannerMsg(false);
      setShowLinkAccNotEligibleBanner(true);
      timeoutId = setTimeout(
        () => setShowLinkAccNotEligibleBanner(false),
        30000
      );
    }

    // Clear the navigation state after reading it
    if (navState?.linkScreenIndicator) {
      window.history.replaceState({}, document.title);
    }

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [linkScreenIndicator, location.state]);
  const productWithCardLast4 = [
    productName,
    accountDetails?.customer?.cardLast4,
  ]
    ?.filter(Boolean)
    ?.join(' ');

  useEffect(() => {
    if (ssoLoginError) {
      setShowErrModal(true);
    }
  }, [ssoLoginError]);

  useEffect(() => {
    if (
      shouldSubmitSSO &&
      (ssoLoginData as any)?.acs_url &&
      (ssoLoginData as any)?.saml_response
    ) {
      // Open a new window with a unique name
      const windowName = 'SSOLoginWindow_' + Date.now();
      const newWindow = window.open('', windowName);

      if (newWindow) {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = (ssoLoginData as any).acs_url;
        form.target = windowName;
        form.style.display = 'none';

        const samlInput = document.createElement('input');
        samlInput.name = 'SAMLResponse';
        samlInput.value = (ssoLoginData as any).saml_response;
        samlInput.type = 'hidden';
        const lhLang = lang === 'fr' ? 'fr-CA' : 'en-US';
        const relayInput = document.createElement('input');
        relayInput.name = 'RelayState';
        relayInput.value = environment.loyaltyHubDashboardUrl + '/' + lhLang;
        relayInput.type = 'hidden';

        form.appendChild(samlInput);
        form.appendChild(relayInput);
        document.body.appendChild(form);
        form.submit();
      }
    }
  }, [ssoLoginData]);

  useEffect(() => {
    if (bankAccountDetail?.bankAccountResponse?.ban) {
      dispatch(
        fetchViewRlhRewardsRequest({
          ban: bankAccountDetail?.bankAccountResponse?.ban,
          accountId: accountId,
        })
      );
    }
  }, [bankAccountDetail?.bankAccountResponse?.ban]);

  const tooltipTextMap = {
    rewards: rb_rewards?.rewardsBalanceIndofText,
    bonus: rb_rewards?.rogersRedemptionBonusAmountInfoText,
  };
  const productTypeName = accountDetails?.productName;

  const cardTypeFlag =
    rb_common?.commonString?.lhProductType?.includes(productTypeName);
  const isEarnRewardsEnabled =
    rb_common?.commonString?.earnRewardProductTypeExclusionList?.includes(
      productTypeName
    );
  const isEntertainmentVisible =
    entries?.rb_feature_flagging?.featureFlagging?.rewards?.entertainmentEntryPointInclusionListWeb?.includes(
      accountDetails?.productName
    ) &&
    entries?.rb_feature_flagging?.featureFlagging?.rewards
      ?.entertainmentEntryWeb === true;
  const rewardTiles = [0, 1, 2, 3, 4]?.reduce((acc: any[], i) => {
    const tile = rb_rewards?.webRewardsTiles.rewardTiles[i];
    let isRLHTile = false;
    let isLATile = false;
    let isEarnRewardTile = false;
    let isEntertainmentTile = false;
    if (i === 1 && !isEarnRewardsEnabled) {
      isEarnRewardTile = true;
    }
    const isRLHConditionsMet =
      loyaltyHubData?.bankAccountResponse?.linkageStatus === '1' &&
      rewardAccountDetails?.redeemerProfile?.accountInfo?.accountStatus === '1';

    if (
      i === 2 &&
      isRLHConditionsMet &&
      entries?.rb_feature_flagging?.featureFlagging?.rewards
        ?.roam_like_home_benefits_web !== false
    ) {
      const isProductExcluded =
        entries?.rb_feature_flagging?.featureFlagging?.rewards?.rlhProductTypeExclusionListWeb?.includes(
          accountDetails?.productName
        );
      if (isProductExcluded) {
        isRLHTile = false;
      } else {
        isRLHTile = true;
      }
    }
    if (i === 2 && !isRLHTile) {
      if (isRLHConditionsMet) {
        isLATile = false;
      } else {
        const isProductExcluded =
          entries?.rb_feature_flagging?.featureFlagging?.rewards?.linkRogersAccountProductTypeExclusionListWeb?.includes(
            accountDetails?.productName
          );
        const isFeatureDisabled =
          entries?.rb_feature_flagging?.featureFlagging?.rewards
            ?.link_rogers_account_web === false;

        if (isProductExcluded || isFeatureDisabled) {
          isLATile = false;
        } else {
          isLATile = true;
        }
      }
    }
    const isSSOUser =
      i === 3 &&
      isUserEligible === true &&
      (ssoRewardLhData as any)?.ssoEligible === true &&
      entries?.rb_feature_flagging?.featureFlagging?.rewards
        ?.loyalty_rewards_hub === true &&
      !cardTypeFlag;

    if (i === 4 && isEntertainmentVisible) {
      isEntertainmentTile = true;
    }

    if (i === 2 && !(isRLHTile || isLATile)) return acc;
    if (i === 3 && !isSSOUser) return acc;
    if (i === 1 && !isEarnRewardTile) return acc;
    if (i === 4 && !isEntertainmentTile) return acc;
    acc.push({
      title: isRLHTile ? tile?.title_linked : tile?.title,
      description: isRLHTile ? tile?.subTitle_linked : tile?.subtitle,
      buttonText: isRLHTile ? tile?.ctaTitle_linked : tile?.ctaTitle,
      img: isRLHTile
        ? assets?.[tile?.imgMappingName_linked]
        : assets?.[tile?.imgMappingName],
      originalIndex: i,
    });
    return acc;
  }, []);

  const handleContinueClick = () => {
    handleClickEvent('Visit Loyalty hub continue');
    if (accountDetails?.productName) {
      setShouldSubmitSSO(true);
      setShowConfirmationModal(false);
      dispatch(
        lhSsoLoginRequest({
          accountId,
          customerId,
          productName: accountDetails?.productName,
          logouturl: environment.loyaltyHubLogoutUrl,
          firstName: accountDetails?.customer?.firstName || '',
          lastName: accountDetails?.customer?.lastName || '',
        })
      );
    }
  };

  const breadcrumbs = [
    { name: entries?.rb_navigation?.homeLogoTitle, href: '/home' },
    { name: entries?.rb_navigation?.rewardsLogoTitle, href: '/rewards' },
  ];
  const [notEligibleBanner, setNotEligibleBanner] = useState(false);
  const [almostThere, setAlmostThere] = useState(false);
  const navigate = useNavigate();
  const isSSOVisible =
    isUserEligible === true &&
    (ssoRewardLhData as any)?.ssoEligible === true &&
    entries?.rb_feature_flagging?.featureFlagging?.rewards
      ?.loyalty_rewards_hub === true &&
    !cardTypeFlag;

  const handleRlhMenuClick = () => {
    if (
      rb_rewards?.webRewardsTiles.rewardTiles[2]?.ctaTitle_linked &&
      viewRlhRewards?.RLH_SCREEN_IND === 'WLS'
    ) {
      navigate('/roam-like-home');
    } else if (
      viewRlhRewards?.RLH_SCREEN_IND === 'RLH ALMOST THERE' ||
      viewRlhRewards?.RLH_SCREEN_IND === 'RLH_ALMOST_THERE'
    ) {
      setAlmostThere(true);
    } else if (
      viewRlhRewards?.RLH_SCREEN_IND === 'WLS_SUSPENDED' ||
      viewRlhRewards?.RLH_SCREEN_IND === 'WLS_IN_ELIGIBLE' ||
      viewRlhRewards?.RLH_SCREEN_IND === 'WLS_IN_ELIGIBLE'
    ) {
      setNotEligibleBanner(true);
    }
  };
  const productName1 = accountDetails?.productName;
  const getFormattedDescription = (): string => {
    return formatRoamLikeHomeText(
      entries?.rb_roam_like_home?.roamLikeHomeContent?.description,
      entries?.rb_roam_like_home?.rbRoamLikeHomeDays,
      productName1
    );
  };
  const getRoamLikeHomeDays = (
    rbRoamLikeHomeDays: Record<string, number> | undefined,
    productName: string | undefined
  ): number => {
    if (!rbRoamLikeHomeDays || !productName1) {
      return 5; // Default value
    }
    return rbRoamLikeHomeDays[productName1] || 5;
  };
  const formatRoamLikeHomeText = (
    text: string | undefined,
    rbRoamLikeHomeDays: Record<string, number> | undefined,
    productName1: string | undefined
  ): string => {
    if (!text) return '';

    const days = getRoamLikeHomeDays(rbRoamLikeHomeDays, productName1);
    return text.replace('%s', days.toString());
  };

  return (
    <div className="bg-bg-body py-4 md:px-8 px-4">
      {showApiErrorBanner && (
        <div className="w-full sm:px-4 md:px-[40px] py-[10px] bg-[#FFF6F3] flex gap-[20px] items-center absolute top-0 left-0 md:left-20 md:w-[stretch]">
          <div className="flex items-center gap-[20px]">
            {' '}
            <div className="flex-shrink-0">
              <img
                src={assets?.infoToolTip}
                alt="info-icon"
                className="w-6 h-6"
              />
            </div>
            <p className="text-[#5B1C00] text-base font-medium">
              {renderDescriptionParagraphs(
                rb_rewards?.webErrorModal?.bannerErrMsg
              )}
            </p>
          </div>
          <div className="flex-grow"></div>
          <div
            className="text-[#DA291C] text-base font-bold cursor-pointer"
            onClick={() => window.location.reload()}
          >
            {renderDescriptionParagraphs(rb_rewards?.webErrorModal?.reload)}
          </div>
          <img
            src={assets?.closeIcon}
            alt="close-icon"
            className="w-6 h-6 cursor-pointer"
            onClick={() => {
              setShowApiErrorBanner(false);
              handleClickEvent('reload');
            }}
          />
        </div>
      )}
      <Breadcrumbs items={breadcrumbs} />
      <div className="w-full m-[22px] mx-auto rounded-[15px] bg-white min-h-[70vh] my-0">
        <div className="bg-white md:p-6 p-4 rounded-lg">
          {loading ||
          bankAccountDetailLoading ||
          rewardsAccLoading ||
          ssoLoginLoading ||
          ssoIntLoading ? (
            <div className="flex justify-center items-center h-[80vh]">
              <Spinner />
            </div>
          ) : (
            <>
              {notEligibleBanner && (
                <div className="mb-5 mt-5">
                  <InlineError
                    errorText={
                      rb_roam_like_home?.webRlhKeys?.notEligibleErrorMsg
                    }
                    aria-live="assertive"
                    fontSize={'text-[15px]'}
                    onClose={() => setNotEligibleBanner(false)}
                    oncloseHide={true}
                  />
                </div>
              )}

              {(showLinkAccStatusBanner || showIgniteBannerMsg) && (
                <div
                  className={`${
                    showLinkAccStatusBanner
                      ? 'bg-[#E5F5F0] border-[#009E69]'
                      : 'bg-[#E5F4F7] border-[#0090AD]'
                  } flex items-start p-4 space-x-3 mb-5 border-l-[6px]`}
                >
                  <div>
                    <p className="font-semibold text-[#1F1F1F] text-base">
                      {showLinkAccStatusBanner
                        ? renderDescriptionParagraphs(
                            rb_linkAccount?.bannerMsgs?.congrats
                          )
                        : renderDescriptionParagraphs(
                            rb_linkAccount?.bannerMsgs?.almostThereTitle
                          )}
                    </p>
                    <p className="mt-1 text-[#1F1F1F] text-base">
                      {showLinkAccStatusBanner ? (
                        getFormattedDescription()
                      ) : (
                        <>
                          {renderDescriptionParagraphs(
                            rb_linkAccount?.bannerMsgs?.almostThereDesc
                          )}{' '}
                          <a
                            href={rb_linkAccount?.bannerMsgs?.websiteLink}
                            className="text-[#2F6FA7] underline"
                            target="_blank"
                            rel="noreferrer"
                          >
                            {renderDescriptionParagraphs(
                              rb_linkAccount?.bannerMsgs?.website
                            )}
                          </a>{' '}
                          {renderDescriptionParagraphs(
                            rb_linkAccount?.bannerMsgs?.almostThereDesc1
                          )}
                        </>
                      )}
                    </p>
                  </div>
                </div>
              )}

              {showLinkAccNotEligibleBanner && (
                <div className="bg-[#FFF6F3] flex items-start p-4 space-x-3 mb-5 border-l-[6px] border-[#DA291C]">
                  <img
                    src={assets?.accNotEligibleIcon}
                    alt="Error Icon"
                    className="w-6 h-6 mt-1"
                  />
                  <div>
                    <p
                      className="font-semibold text-[#1F1F1F] text-base"
                      aria-label={
                        renderDescriptionParagraphs(
                          rb_linkAccount?.bannerMsgs?.accNotEligibleTitle ||
                            'Account not eligible to link',
                          ' ',
                          true
                        ) as string
                      }
                    >
                      {renderDescriptionParagraphs(
                        rb_linkAccount?.bannerMsgs?.accNotEligibleTitle ||
                          'Account not eligible to link'
                      )}
                    </p>
                    <p
                      className="mt-1 text-[#1F1F1F] text-base"
                      aria-label={
                        renderDescriptionParagraphs(
                          rb_linkAccount?.bannerMsgs?.accNotEligibleDesc ||
                            'You must have a Rogers wireless account to receive your 5 Roam Like Home rewards every year. If you have another Rogers account go back and sign in with those credentials.',
                          ' ',
                          true
                        ) as string
                      }
                    >
                      {renderDescriptionParagraphs(
                        rb_linkAccount?.bannerMsgs?.accNotEligibleDesc ||
                          'You must have a Rogers wireless account to receive your 5 Roam Like Home rewards every year. If you have another Rogers account go back and sign in with those credentials.'
                      )}
                    </p>
                  </div>
                </div>
              )}

              <h2 className="text-[22px] font-semibold leading-[28px] tracking-[0.35%] md:pb-7 pb-1">
                {entries?.rb_navigation?.rewardsLogoTitle}
              </h2>

              <div className="flex flex-col gap-4 lg:flex-row mb-4">
                <div className="block md:hidden">
                  <p className="font-normal text-lg mt-4">
                    {renderDescriptionParagraphs(productWithCardLast4)}
                  </p>
                </div>

                <div
                  className={`md:flex hidden flex-row items-center gap-4 bg-[#F5F5F5] rounded-[10px] p-4 w-full ${
                    accountDetails?.productName !==
                      rb_common.commonString.fidoProductName &&
                    accountDetails?.productName !==
                      rb_common.commonString.platinumProductName
                      ? 'lg:w-3/4'
                      : 'lg:w-full'
                  }`}
                >
                  <img
                    src={cardStructure?.imgUrl}
                    alt="no-Card"
                    className="h-[100px]"
                  />
                  <div className="pl-4">
                    <div className="text-[#1F1F1F] text-[15px] font-normal leading-[24px]">
                      {rb_transaction_history?.showingTitle}
                    </div>
                    <div className="text-[#1F1F1F] text-[16px] font-semibold leading-[24px]">
                      {renderDescriptionParagraphs(productWithCardLast4)}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-4 md:grid md:grid-cols-2 md:gap-4 lg:flex lg:flex-row w-full">
                  <FlippedToolTip
                    tileId="rewards"
                    title={rb_rewards?.rewardsBalanceTitle}
                    value={
                      rewardBalance === null || rewardBalance === undefined
                        ? lang?.includes('en')
                          ? '$-.--'
                          : '-.--$'
                        : Number(rewardBalance) < 0
                          ? `-${webFormatCurrency(Math.abs(Number(rewardBalance)), lang)}`
                          : webFormatCurrency(Number(rewardBalance), lang) ||
                            '--'
                    }
                    icon={assets?.rb_bonus_icon}
                    tooltipTextMap={tooltipTextMap}
                    lang={lang}
                    activeTile={activeTile}
                    setActiveTile={setActiveTile}
                    showTooltipMobile={showTooltipMobile}
                    setShowTooltipMobile={setShowTooltipMobile}
                  />
                </div>
              </div>

              <div
                className={`grid gap-6 ${isEntertainmentVisible || isSSOVisible ? 'xl:grid-cols-4' : 'xl:grid-cols-3'} grid-cols-1 md:grid-cols-2 lg:grid-cols-2`}
              >
                {rewardTiles.map((card, index) => {
                  const redirectPaths = ['/redeem-rewards', '/view-rewards'];
                  return (
                    <ActiveRewardTile
                      disabled={card.disabled}
                      key={index}
                      img={card.img}
                      title={card.title}
                      description={card.description}
                      buttonText={card.buttonText}
                      customStyle="flex flex-row xl:flex-col lg:flex-row md:flex-col 2x1:flex-col items-start xl:items-center md:items-center lg:items-start p-[10px] xl:p-[30px] md:p-[30px] lg:p-[30px] pt-[20px] md:gap-[30px] gap-[10px] border border-[#E9E9E9] rounded-lg bg-white sm:flex-col sm:justify-center sm:item-center"
                      redirectTo={
                        card.originalIndex === 3
                          ? undefined
                          : card.originalIndex === 4
                            ? '/world-legend-credit-redemption'
                            : redirectPaths[card.originalIndex]
                      }
                      onClick={
                        card.originalIndex === 3
                          ? () => {
                              setShowConfirmationModal(true);
                              handleClickEvent('Visit Loyalty hub');
                            }
                          : card.originalIndex === 2
                            ? () => {
                                const isLinked =
                                  loyaltyHubData?.bankAccountResponse
                                    ?.linkageStatus === '1' &&
                                  rewardAccountDetails?.redeemerProfile
                                    ?.accountInfo?.accountStatus === '1';

                                if (isLinked) {
                                  handleRlhMenuClick(); // this navigates to /roam-like-home or handles errors
                                  handleClickEvent('roam like home');
                                } else {
                                  navigate('/link-account');
                                  handleClickEvent('link account');
                                }
                              }
                            : undefined
                      }
                    />
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      <CommanModal
        isOpen={showErrModal || showConfirmationModal}
        jsxContent={
          <>
            <div
              className="mt-[20px] text-lg font-semibold text-center"
              aria-label={renderDescriptionParagraphs(
                showErrModal
                  ? rb_rewards?.webErrorModal?.errModalTitle
                  : rb_rewards?.webErrorModal?.confirmModalTitle,
                undefined,
                true
              )}
            >
              {showErrModal
                ? renderDescriptionParagraphs(
                    rb_rewards?.webErrorModal?.errModalTitle
                  )
                : renderDescriptionParagraphs(
                    rb_rewards?.webErrorModal?.confirmModalTitle
                  )}
            </div>
            <div
              className="mt-[20px]"
              aria-label={renderDescriptionParagraphs(
                showErrModal
                  ? rb_rewards?.webErrorModal?.errModalDesc
                  : rb_rewards?.webErrorModal?.confirmModalDesc,
                undefined,
                true
              )}
            >
              {showErrModal
                ? renderDescriptionParagraphs(
                    rb_rewards?.webErrorModal?.errModalDesc
                  )
                : renderDescriptionParagraphs(
                    rb_rewards?.webErrorModal?.confirmModalDesc
                  )}
            </div>
          </>
        }
        onClose={() => {
          showErrModal
            ? setShowErrModal(false)
            : setShowConfirmationModal(false);
          handleClickEvent('Stay');
        }}
        buttonText={
          showErrModal
            ? rb_rewards?.webErrorModal?.okCta
            : rb_rewards?.webErrorModal?.stayCta
        }
        extraButtonText={
          showErrModal ? undefined : rb_rewards?.webErrorModal?.continueCta
        }
        onExtraButtonClick={showErrModal ? undefined : handleContinueClick}
        cardStatus={true}
        closeIcon={false}
      />

      {/* //RLH almost there poup */}
      <CommanModal
        width={'w-[70%] md:w-[40%] xl:w-[35%] lg:w-[35%]'}
        padding="p-0"
        isOpen={almostThere}
        jsxContent={
          <>
            <img
              src={assets?.almost_there_image}
              alt="Group of people enjoying travel"
              className="w-full"
            />
            <div className="px-4 py-6">
              <div className="flex flex-row gap-2 items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 25"
                  fill="none"
                >
                  <path
                    d="M20.985 3.56578C19.715 2.29578 17.655 2.29578 16.385 3.56578L13.975 5.97578L6.35504 4.65578C6.03504 4.59578 5.70504 4.70578 5.46504 4.93578L4.13504 6.26578C3.64504 6.75578 3.78504 7.58578 4.41504 7.88578L9.58504 10.3658L7.47504 12.4758L2.95504 12.8958C1.93504 12.9958 1.68504 14.3658 2.61504 14.8158L7.42504 17.1258L9.63504 21.7358L9.73504 21.9358C10.175 22.8558 11.555 22.6158 11.655 21.5958L12.075 17.0758L14.185 14.9658L16.665 20.1358C16.965 20.7658 17.795 20.9058 18.285 20.4158L19.615 19.0858C19.845 18.8558 19.955 18.5258 19.895 18.1958L18.575 10.5758L20.985 8.16578C22.255 6.89578 22.255 4.83578 20.985 3.56578ZM19.575 6.74578L16.805 9.51578C16.575 9.74578 16.465 10.0758 16.525 10.4058L17.835 17.9158L15.395 12.8358C15.095 12.2058 14.265 12.0658 13.775 12.5558L10.425 15.9058C10.255 16.0758 10.155 16.2958 10.135 16.5258L10.015 17.8458L9.10504 15.9458L8.96504 15.7458L8.91504 15.6758C8.83504 15.5958 8.74504 15.5258 8.63504 15.4758L6.73504 14.5658L8.04504 14.4458C8.27504 14.4258 8.49504 14.3258 8.66504 14.1558L12.015 10.8058C12.505 10.3158 12.365 9.48578 11.735 9.18578L6.65504 6.74578L14.165 8.05578C14.485 8.11578 14.815 8.00578 15.055 7.77578L17.825 5.00578C18.315 4.51578 19.105 4.51578 19.595 5.00578C20.085 5.49578 20.085 6.28578 19.595 6.77578L19.575 6.74578Z"
                    fill="#1F1F1F"
                  />
                </svg>
                <p
                  className="text-black text-[16px] font-[500]"
                  aria-label={renderDescriptionParagraphs(
                    rb_roam_like_home?.almostThereScreen?.almost_there_screen
                      ?.page_content?.title,
                    undefined,
                    true
                  )}
                >
                  {
                    rb_roam_like_home?.almostThereScreen?.almost_there_screen
                      ?.page_content?.title
                  }
                </p>
              </div>
              <p
                className="text-black text-[16px] font-[450]"
                aria-label={
                  rb_roam_like_home?.almostThereScreen?.almost_there_screen
                    ?.page_content?.description
                }
              >
                {renderDescriptionParagraphs(
                  rb_roam_like_home?.almostThereScreen?.almost_there_screen
                    ?.page_content?.description
                )}
                {renderDescriptionParagraphs(
                  rb_roam_like_home?.almostThereScreen?.almost_there_screen
                    ?.page_content?.subDescription
                )}
              </p>
            </div>
          </>
        }
        onClose={() => {
          setAlmostThere(false);
        }}
        buttonText={
          rb_roam_like_home?.almostThereScreen?.almost_there_screen?.buttons
            ?.close
        }
        extraButtonText={
          rb_roam_like_home?.notEligibleScreen?.page_content?.learn_more_label
        }
        onExtraButtonClick={() => {
          window.open(
            `${rb_roam_like_home?.almostThereScreen?.almost_there_screen?.page_content?.link_url}`,
            '_blank'
          );
        }}
        cardStatus={true}
        closeIcon={false}
        buttonClassName="flex-row px-4 pb-4 mt-0"
      />
    </div>
  );
};

export default Rewards;
